import { useState } from 'react';
import { get } from 'lodash';
import { makeStyles } from '@mui/styles';
import { Divider } from '@mui/material';

import { NOTE_ACTIONS, StaffNote, StaffNoteComment } from './Types';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { NewCommentForm } from './NewCommentForm';
import clsx from 'clsx';
import DeleteNoteConfirmModal from './DeleteNoteConfirmModal';

const useStyles: any = makeStyles(() => ({
  root: {},
  commentBtn: {
    color: '#055577',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '16px',
    cursor: 'pointer'
  },
  commentBtnSm: {
    color: '#055577',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '22px',
    cursor: 'pointer',
    display: 'inline-block',
    margin: '0 4px 0 0'
  },
  commentBtnDisable: {
    color: '#666'
  },
  divider: {
    paddingRight: '96%',
    display: 'inline-block'
  },
  comment: {
    padding: '8px 12px 3px',
    fontSize: 16,
    borderRadius: 8,
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row'
  },
  replyContainer: {
    paddingLeft: 48
  },
  createdByInitials: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginRight: 12,
    color: '#999',
    backgroundColor: '#ECECEC',
    border: '1px solid #DADADA',
    borderRadius: 4,
    verticalAlign: 'middle',
    textTransform: 'capitalize',
    fontSize: 12,
    fontWeight: 500
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  bottomDeets: {
    fontSize: 12,
    fontWeight: 501,
    lineHeight: '20px',
    padding: '0 4px'
  },
  createdByName: {
    minHeight: 20,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '24px',
    cursor: 'default'
  },
  content: {
    color: '#000',
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '20px',
    cursor: 'default'
  },
  deleteAndDeets: {
    fontSize: 12,
    paddingRight: 4
  },
  firstCommentFormContainer: {
    marginTop: 4
  },
  addToExistingCommentsFormContainer: {
    marginLeft: 48
  }
}));

export interface DeleteNoteCommentApiResponse {
  data: {
    data: {
      note: StaffNoteComment;
      success?: boolean;
    };
  };
}

export interface NoteCommentsProps {
  note: StaffNote;
  actionCallback: (
    note: StaffNote,
    action: keyof typeof NOTE_ACTIONS,
    data?: any
  ) => void;
}

export const apiSuffix = '/staff/user-note';

const NoteComments = (props: NoteCommentsProps) => {
  const { note, actionCallback } = props;

  const classes = useStyles();

  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number>(null);
  const [confirmCommentDeleteModalOpen, setConfirmCommentDeleteModalOpen] =
    useState(false);

  const comments = note.comments || [];
  const hasComments = comments.length > 0;

  const setShowComments = (show: boolean) => {
    actionCallback(
      note,
      show ? NOTE_ACTIONS.SHOW_COMMENTS : NOTE_ACTIONS.HIDE_COMMENTS
    );
  };

  return (
    <div className={classes.root}>
      {!hasComments && !showAddForm && (
        <div
          onClick={() => {
            setShowAddForm(true);
          }}
          className={classes.commentBtn}
        >
          Add a comment
        </div>
      )}
      {hasComments && !note.showComments && (
        <div
          onClick={() => setShowComments(true)}
          className={classes.commentBtn}
        >{`Show ${comments.length} Comment${
          comments.length > 1 ? 's' : ''
        }`}</div>
      )}
      {hasComments && note.showComments && (
        <div
          onClick={() => {
            setShowComments(false);
            setShowAddForm(false);
          }}
          className={classes.commentBtn}
        >{`Hide ${comments.length} Comment${
          comments.length > 1 ? 's' : ''
        }`}</div>
      )}
      {hasComments && note.showComments && (
        <>
          <Divider className={classes.divider} />
          {comments.map(comment => {
            const commentIsDeleging = deleteTargetId === comment.id;
            return (
              <div className={classes.comment} key={comment.id}>
                <div>
                  <span className={classes.createdByInitials}>
                    {comment.createdBy
                      ? `${get(comment.createdBy?.firstName, '[0]', '')} ${get(
                          comment.createdBy?.lastName,
                          '[0]',
                          ''
                        )}`
                      : ''}
                  </span>
                </div>
                <div className={classes.details}>
                  <span className={classes.createdByName}>
                    {comment.createdBy
                      ? `${comment.createdBy?.firstName} ${comment.createdBy?.lastName}`
                      : ''}
                  </span>
                  <span className={classes.content}>{comment.content}</span>
                  <span className={classes.deleteAndDeets}>
                    {commentIsDeleging ? (
                      <span
                        className={clsx([
                          classes.commentBtnSm,
                          classes.commentBtnDisable
                        ])}
                      >
                        Deleting
                      </span>
                    ) : (
                      <span
                        onClick={() => {
                          setDeleteTargetId(comment.id);
                          setConfirmCommentDeleteModalOpen(true);
                        }}
                        className={classes.commentBtnSm}
                      >
                        Delete
                      </span>
                    )}
                    &bull;
                    <span className={classes.bottomDeets}>
                      {comment?.store ? comment?.store.code : ''}
                    </span>
                    &bull;
                    <span className={classes.bottomDeets}>
                      {comment.createdAt
                        ? `${convertToStoreLocalTime(
                            comment.createdAt,
                            'MM/DD/yyyy'
                          )} at ${convertToStoreLocalTime(
                            comment.createdAt,
                            'h:mm A'
                          )}`
                        : ''}
                    </span>
                  </span>
                </div>
              </div>
            );
          })}
        </>
      )}
      {hasComments && note.showComments && !showAddForm && (
        <div className={classes.replyContainer}>
          <span
            onClick={() => {
              setShowAddForm(true);
            }}
            className={classes.commentBtn}
          >
            Reply
          </span>
        </div>
      )}
      {showAddForm && (
        <div
          className={
            hasComments
              ? classes.addToExistingCommentsFormContainer
              : classes.firstCommentFormContainer
          }
        >
          <NewCommentForm
            note={note}
            actionCallback={actionCallback}
            hideForm={() => {
              setShowAddForm(false);
            }}
          />
        </div>
      )}
      {confirmCommentDeleteModalOpen && (
        <DeleteNoteConfirmModal
          isOpen={confirmCommentDeleteModalOpen}
          isComment={true}
          commentId={deleteTargetId}
          setIsOpen={open => {
            setConfirmCommentDeleteModalOpen(open);
            setDeleteTargetId(null);
          }}
          note={note}
          actionCallback={actionCallback}
        />
      )}
    </div>
  );
};

export default NoteComments;
